import { useRef, type ComponentProps, type ReactNode } from 'react';
import { ModalTitle } from '@overlayqa/shared/ui';
// The integration dialogs' title style (`.oq-dialog__title`, 16px, one line): one title size per page (2026-11-02).
import '@overlayqa/shared/ui/dialog.css';
import type { HugeiconsIcon } from '@hugeicons/react';
import { ModalPortal } from '../shared/ModalPortal';
import { useModalA11y } from '../../hooks/useModalA11y';

interface SetupDialogProps {
  title: string;
  icon: ComponentProps<typeof HugeiconsIcon>['icon'];
  /** The tool's own brand tile in the title, as every integration dialog opens (falls back to the icon). */
  mark?: ReactNode;
  /** Called by Close, Escape and the backdrop; a caller with a save in flight ignores it. */
  onClose: () => void;
  closeDisabled?: boolean;
  children: ReactNode;
}

/** The setup dialog every tool card on /integrations opens (AI tools, Zapier, Make). */
export function SetupDialog({ title, icon, mark, onClose, closeDisabled = false, children }: SetupDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, onClose);
  return <ModalPortal><div ref={dialogRef} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true"
    aria-labelledby="setup-dialog-title" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="bg-white rounded-xl w-full max-w-xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
      <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
        <ModalTitle icon={icon} mark={mark} id="setup-dialog-title" className="oq-dialog__title">{title}</ModalTitle>
        <button type="button" onClick={onClose} disabled={closeDisabled} className="text-gray-400 hover:text-gray-600 transition-colors p-2" aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-6 pt-4 pb-5 overflow-y-auto">{children}</div>
    </div>
  </div></ModalPortal>;
}
